import axios from 'axios';
import type {
  StorjBackupListResponse,
  StorjBackupDeleteResponse,
  ResetInventoryCountsResponse,
  CompleteAllCountsResponse,
  SeedInventoryTransactionLogResponse,
  AuditShelfResponse,
  UpdateShelfAuditResponse,
  ResetShelfAuditResponse,
  StartShelfAuditResponse,
  EndShelfAuditResponse,
  CreateShelfAuditEventResponse,
  ClaimShelfAuditResponse,
  ReleaseShelfAuditResponse,
  ClaimNextShelfAuditResponse,
  ShelfCheckResponse,
  ShelfManageResponse,
  OrderLookupResponse,
  OcrAbCompareResponse,
} from '../types/apiTypes';
import { validateResponse } from './validateResponse';
import {
  StorjBackupListResponseSchema,
  AuditShelfResponseSchema,
  UpdateShelfAuditResponseSchema,
  ResetShelfAuditResponseSchema,
  StartShelfAuditResponseSchema,
  EndShelfAuditResponseSchema,
  CreateShelfAuditEventResponseSchema,
  ClaimShelfAuditResponseSchema,
  ReleaseShelfAuditResponseSchema,
  ClaimNextShelfAuditResponseSchema,
  PreviewLabelResponseSchema,
  PrinterStatusProbeResponseSchema,
  PosLabelFormatsResponseSchema,
  PrintPosLabelFormatResponseSchema,
  OcrAbCompareResponseSchema,
} from '../schemas/apiResponse.schemas';
import type {
  PreviewLabelResponse,
  PrinterStatusProbeResponse,
  PosLabelFormatsResponse,
  PrintPosLabelFormatResponse,
} from '../types/apiTypes';
import type { DestinationPrinterId } from '@/shared/types/printer.types';
import type { ShelfAuditHeightLimit } from '@/shared/types/shelfAudit.types';

const API_BASE = '/api/dev';

export const fetchShopifyInventoryItem = async (inventoryItemId: string): Promise<Record<string, unknown>> => {
  const response = await axios.get(`${API_BASE}/shopify-inventory-item/${inventoryItemId}`);
  return response.data;
};

export const fetchStorjBackupList = async (): Promise<StorjBackupListResponse> => {
  const response = await axios.get(`${API_BASE}/storj-backup/list`);
  return validateResponse(StorjBackupListResponseSchema, response.data, 'GET /api/dev/storj-backup/list');
};

export const deleteStorjBackupObject = async (key: string): Promise<StorjBackupDeleteResponse> => {
  const response = await axios.delete(`${API_BASE}/storj-backup`, { data: { key } });
  return response.data;
};

/** Reset inventory counts: truncate cycle_count_events and reset eligibility to defaults. */
export const resetInventoryCounts = async (): Promise<ResetInventoryCountsResponse> => {
  const response = await axios.post(`${API_BASE}/reset-inventory-counts`);
  return response.data;
};

/** Complete all open counts: set actual = expected, mark complete, update eligibility. */
export const completeAllCounts = async (): Promise<CompleteAllCountsResponse> => {
  const response = await axios.post(`${API_BASE}/complete-all-counts`);
  return response.data;
};

/** Insert a placeholder row into inventory.inventory_transaction_logs. */
export const seedInventoryTransactionLog = async (): Promise<SeedInventoryTransactionLogResponse> => {
  const response = await axios.post(`${API_BASE}/inventory-transaction-logs/seed`);
  return response.data;
};

/** Look up a ShipStation order by order number (any status). Returns orderId + orderNumber. */
export const lookupOrderByNumber = async (orderNumber: string): Promise<OrderLookupResponse> => {
  const response = await axios.get(`${API_BASE}/orders/lookup`, { params: { orderNumber } });
  return response.data;
};

export const fetchShelves = async (): Promise<AuditShelfResponse> => {
  const response = await axios.get('/api/shelves/audit');
  return validateResponse(AuditShelfResponseSchema, response.data, 'GET /api/shelves/audit');
};

export const updateShelfAuditFlag = async (id: number | string, auditFlag: boolean | null): Promise<UpdateShelfAuditResponse> => {
  const response = await axios.patch(`/api/shelves/${id}/audit-flag`, { audit_flag: auditFlag });
  return validateResponse(UpdateShelfAuditResponseSchema, response.data, 'PATCH /api/shelves/:id/audit-flag');
};

/** Admin-only: clear marks/claims and truncate events for the open audit only. */
export const resetShelfAudit = async (): Promise<ResetShelfAuditResponse> => {
  const response = await axios.post('/api/shelves/audit/reset');
  return validateResponse(ResetShelfAuditResponseSchema, response.data, 'POST /api/shelves/audit/reset');
};

/** Admin-only: open a new named audit (Audit N). */
export const startShelfAudit = async (): Promise<StartShelfAuditResponse> => {
  const response = await axios.post('/api/shelves/audit/start');
  return validateResponse(StartShelfAuditResponseSchema, response.data, 'POST /api/shelves/audit/start');
};

/** Admin-only: close the open audit without wiping events/flags/claims. */
export const endShelfAudit = async (): Promise<EndShelfAuditResponse> => {
  const response = await axios.post('/api/shelves/audit/end');
  return validateResponse(EndShelfAuditResponseSchema, response.data, 'POST /api/shelves/audit/end');
};

/** Record expected vs existing storage-id prefixes for a shelf rescan confirm (replaces prior event). */
export const createShelfAuditEvent = async (input: {
  shelf_name: string;
  shelf_id?: number | null;
  expected_ids: string[];
  existing_ids: string[];
}): Promise<CreateShelfAuditEventResponse> => {
  const response = await axios.post('/api/shelves/audit/events', input);
  return validateResponse(
    CreateShelfAuditEventResponseSchema,
    response.data,
    'POST /api/shelves/audit/events'
  );
};

/** Claim a shelf for exclusive audit work. */
export const claimShelfAudit = async (shelfId: number): Promise<ClaimShelfAuditResponse> => {
  const response = await axios.post('/api/shelves/audit/claim', { shelf_id: shelfId });
  return validateResponse(ClaimShelfAuditResponseSchema, response.data, 'POST /api/shelves/audit/claim');
};

/** Release the current user's shelf claim(s). */
export const releaseShelfAudit = async (shelfId?: number): Promise<ReleaseShelfAuditResponse> => {
  const response = await axios.post(
    '/api/shelves/audit/release',
    shelfId != null ? { shelf_id: shelfId } : {}
  );
  return validateResponse(ReleaseShelfAuditResponseSchema, response.data, 'POST /api/shelves/audit/release');
};

/** Claim the next unaudited shelf after confirming the current one. */
export const claimNextShelfAudit = async (
  afterShelfId: number,
  heightLimit?: ShelfAuditHeightLimit | null,
): Promise<ClaimNextShelfAuditResponse> => {
  const response = await axios.post('/api/shelves/audit/claim-next', {
    after_shelf_id: afterShelfId,
    ...(heightLimit ? { height_limit: heightLimit } : {}),
  });
  return validateResponse(
    ClaimNextShelfAuditResponseSchema,
    response.data,
    'POST /api/shelves/audit/claim-next'
  );
};

export const checkShelfExists = async (shelfName: string): Promise<ShelfCheckResponse> => {
  const response = await axios.post('/api/shelves/check', { shelf_name: shelfName });
  return response.data;
};

export const manageShelf = async (shelfName: string, action: 'create' | 'delete'): Promise<ShelfManageResponse> => {
  const response = await axios.post('/api/shelves/manage', { shelf_name: shelfName, action });
  return response.data;
};

export type PreviewWarehouseLabelRequest = {
  kind: 'warehouse';
  items: Array<{ sku: string }>;
};

export type PreviewBigLabelRequest = {
  kind: 'big';
  sku: string;
  baseQty?: string | number | null;
};

export type PreviewLabelRequest = PreviewWarehouseLabelRequest | PreviewBigLabelRequest;

/** Dev-only: render warehouse or big label ZPL to PNG via Labelary (no print / no DB). */
export const previewLabel = async (body: PreviewLabelRequest): Promise<PreviewLabelResponse> => {
  const response = await axios.post(`${API_BASE}/preview-label`, body);
  return validateResponse(PreviewLabelResponseSchema, response.data, 'POST /api/dev/preview-label');
};

/** Dev-only: query printer status via the relay. Never prints. */
export const probePrinterStatus = async (
  printerId: DestinationPrinterId,
): Promise<PrinterStatusProbeResponse> => {
  const response = await axios.post(`${API_BASE}/printer-status`, { printerId });
  return validateResponse(PrinterStatusProbeResponseSchema, response.data, 'POST /api/dev/printer-status');
};

/** Dev-only: list SKU label layouts saved from Shopify Admin in sales.pos_label_formats. */
export const fetchPosLabelFormats = async (): Promise<PosLabelFormatsResponse> => {
  const response = await axios.get(`${API_BASE}/pos-label-formats`);
  return validateResponse(PosLabelFormatsResponseSchema, response.data, 'GET /api/dev/pos-label-formats');
};

/** Dev-only: print a sample of a saved Shopify Admin SKU label format via the warehouse relay. */
export const printPosLabelFormat = async (
  shop: string,
  printerId: DestinationPrinterId,
): Promise<PrintPosLabelFormatResponse> => {
  const response = await axios.post(`${API_BASE}/pos-label-formats/print`, { shop, printerId });
  return validateResponse(
    PrintPosLabelFormatResponseSchema,
    response.data,
    'POST /api/dev/pos-label-formats/print',
  );
};

/** Dev-only: paired OCR A/B — TEXT_DETECTION vs DOCUMENT_TEXT_DETECTION on the same image. */
export const runOcrAbCompare = async (image: string): Promise<OcrAbCompareResponse> => {
  const response = await axios.post(`${API_BASE}/ocr-ab`, { image });
  return validateResponse(OcrAbCompareResponseSchema, response.data, 'POST /api/dev/ocr-ab');
};
